import React from "react";
import PropTypes from "prop-types";
import { getFormValues, change } from "redux-form";
import { useDispatch, useSelector } from "react-redux";

import { Icon } from "rhombus-react";

import { CAROUSEL_AD_TYPES } from "utils/constants";
import { MAX_CAROUSEL_CARDS_BY_ADTYPE } from "social/constants/common";
import { getMinNumCards } from "social/utils";
import { useTooltipRebuild } from "hooks/useTooltipRebuild";

import { SOCIAL_WIZARD } from "widgets/forms/list";

import { getNewClientID } from "utils/common";

import "./CardHeader.css";

export default function CardHeader(props) {
	const dispatch = useDispatch();
	const {
		adType,
		isViewOnly,
		deleteCardTooltip,
		duplicateCardTooltip,
		title = "Card",
	} = props;

	const minCards = getMinNumCards(adType);
	const values = useSelector(state => getFormValues(SOCIAL_WIZARD)(state)?.[adType]);

	const { currentCard = 0, cards } = values || {};
	const numCards = cards?.length;
	const maxCards = MAX_CAROUSEL_CARDS_BY_ADTYPE[adType];

	const currentCardField = `${adType}.currentCard`;
	const cardsField = `${adType}.cards`;

	function incrementCard(){
		if(currentCard < numCards - 1){
			dispatch(change(SOCIAL_WIZARD, currentCardField, currentCard + 1));
		}
	}

	function decrementCard(){
		if(currentCard > 0){
			dispatch(change(SOCIAL_WIZARD, currentCardField, currentCard - 1));
		}
	}

	function duplicateCard(){
		const newCard = {
			...cards[currentCard],
			order: numCards,
			clientId: getNewClientID(),
		};
		delete newCard.id;
		dispatch(change(SOCIAL_WIZARD, cardsField, [...cards, newCard]));
		dispatch(change(SOCIAL_WIZARD, currentCardField, numCards));
	}

	function deleteCard(){
		const newCards = [
			...cards.slice(0, currentCard),
			...cards.slice(currentCard + 1).map(card => ({ ...card, order: card.order - 1 })),
		];
		if(currentCard !== 0){
			dispatch(change(SOCIAL_WIZARD, currentCardField, currentCard - 1));
		}
		dispatch(change(SOCIAL_WIZARD, cardsField, newCards));
	}

	useTooltipRebuild(currentCard);

	return (
		<div className="card-header-container">
			<span className="card-header-title">{title} {currentCard + 1}</span>
			<div className="card-header-actions">
				<button
					data-tip="Previous Card"
					disabled={currentCard === 0}
					onClick={decrementCard}
					className="cypress-prev-card"
				>
					<Icon
						name="far fa-arrow-left"
						className="prev-arrow"
					/>
				</button>
				<button
					data-tip="Next Card"
					disabled={currentCard >= numCards - 1}
					onClick={incrementCard}
					className="cypress-next-card"
				>
					<Icon
						name="far fa-arrow-right"
						className="next-arrow"
					/>
				</button>
				<button
					data-tip={duplicateCardTooltip}
					disabled={isViewOnly || (numCards >= maxCards)}
					onClick={duplicateCard}
					className="cypress-duplicate-card"
				>
					<Icon
						name="duplicate"
						className="duplicate"
					/>
				</button>
				<button
					data-tip={deleteCardTooltip}
					disabled={isViewOnly || (numCards <= minCards)}
					onClick={deleteCard}
					className="cypress-delete-card"
				>
					<Icon
						name="far fa-trash"
						className="delete"
					/>
				</button>
			</div>
		</div>
	);
}

CardHeader.displayName = "CardHeader";

CardHeader.defaultProps = {
	deleteCardTooltip: "Delete Card",
	duplicateCardTooltip: "Duplicate Card",
};

CardHeader.propTypes = {
	adType: PropTypes.oneOf(CAROUSEL_AD_TYPES),
	isViewOnly: PropTypes.bool,
	deleteCardTooltip: PropTypes.string,
	duplicateCardTooltip: PropTypes.string,
};
